import { Badge, Tooltip, TooltipTrigger } from "@light-technology/components";
import { ArrowRight, Sun, Users01 } from "@untitledui/icons";
import { Link } from "react-router";
import { Leaf01 } from "../assets/leaf-01";
import { type AppResponse, type PlanResponse } from "../state/api.types";
import { compactFormatter } from "../utilities/analyticsHelpers";
import { planDetailsLink } from "../utilities/links";
import { ProgramTypeIcon } from "./ProgramTypeIcon";
import { HoverCard } from "./shared/HoverCard";

type PlansPageProps = {
  app: AppResponse;
  plan: PlanResponse;
  activeAccounts?: number;
};

export const PlanCard = (props: PlansPageProps) => {
  const { app, plan, activeAccounts } = props;
  return (
    <Link to={planDetailsLink(app.slug, plan.uuid)}>
      <HoverCard className="h-[200px] w-[300px]">
        <div className="flex w-full flex-col justify-between">
          <div className="flex w-full flex-col gap-4">
            <div className="flex flex-row justify-between">
              <Icons {...props} />
              <StatusCard {...props} />
            </div>
            <div className="flex flex-col gap-1">
              <div
                className="text-primary-900 truncate text-xl font-semibold"
                title={plan.name}
              >
                {plan.name}
              </div>
              {plan.description && (
                <div
                  className="line-clamp-2 text-sm text-secondary"
                  title={plan.description}
                >
                  {plan.description}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-1 text-secondary">
              <Users01 className="size-5" />
              <span className="font-mono text-xl">
                {compactFormatter.format(activeAccounts ?? 0)}
              </span>
            </div>
            <ArrowRight className="size-5 text-secondary" />
          </div>
        </div>
      </HoverCard>
    </Link>
  );
};

const Icons = ({ plan }: PlansPageProps) => {
  const isRenewable = plan.renewable_percent === 100;
  return (
    <div className="flex flex-row gap-2">
      {isRenewable && (
        <Tooltip title="100% renewable energy" placement="bottom">
          <TooltipTrigger>
            <Leaf01 className="max-h-6" />
          </TooltipTrigger>
        </Tooltip>
      )}
      {plan.has_solar_buyback && (
        <Tooltip title="Includes solar buyback" placement="bottom">
          <TooltipTrigger>
            <Sun className="max-h-6" />
          </TooltipTrigger>
        </Tooltip>
      )}
      {plan.linked_programs.map((program) => (
        <Tooltip key={program.uuid} title={program.name} placement="bottom">
          <TooltipTrigger>
            <ProgramTypeIcon
              programType={program.program_type}
              className="max-h-6"
            />
          </TooltipTrigger>
        </Tooltip>
      ))}
    </div>
  );
};

const StatusCard = ({ plan }: PlansPageProps) => {
  if (plan.state === "active") {
    return (
      <Badge size="md" color="success" type="pill-color">
        Live
      </Badge>
    );
  }
  return (
    <Badge size="md" color="gray" type="pill-color">
      {plan.state === "draft" ? "Draft" : "Archived"}
    </Badge>
  );
};
